import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { demo, StatusBadge, SyntheticTag } from "./c1lib";
import { Section } from "./PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Check, CalendarCheck, Upload, SendHorizonal, ArrowRight, Lock } from "lucide-react";

const EVIDENCE_TYPES = ["TEMPLE_RECEIPT", "FULFILMENT_STATEMENT", "FULFILMENT_PHOTO", "VALIDATOR_NOTE"];

export default function TempleManager() {
  const [rows, setRows] = useState([]);
  const [rid, setRid] = useState(null);
  const [d, setD] = useState(null);

  const loadList = useCallback(() => { demo.list().then(setRows).catch(() => {}); }, []);
  const loadRec = useCallback(() => { if (rid) demo.record(rid).then(setD).catch(() => {}); }, [rid]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadRec(); }, [loadRec]);

  const refresh = () => { loadList(); loadRec(); };

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } };

  return (
    <Section title="Temple Manager" subtitle="Accept a request, record fulfilment, attach the temple receipt and evidence, then submit for validation. Recording fulfilment does not certify — validation is separate.">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-3">Sankalpa requests</h3>
          <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="manager-list">
            {rows.length === 0 && <div className="p-6 text-slate-400 text-sm">No records. Create one as a Devotee.</div>}
            {rows.map((r) => (
              <button key={r.record_id} onClick={() => setRid(r.record_id)} data-testid={`manager-row-${r.record_id}`}
                className={`w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3 ${rid === r.record_id ? "bg-slate-50" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="font-mono text-[11px] text-slate-600">{r.record_id}</span>{r.is_reference && <Lock size={11} className="text-slate-400" />}</div>
                  <div className="text-sm text-slate-700 truncate">{r.seva_name}</div>
                </div>
                <StatusBadge status={r.status} />
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {!d ? <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-slate-400 text-sm">Select a request to manage.</div>
            : <ManagerPanel d={d} act={act} refresh={refresh} />}
        </div>
      </div>
    </Section>
  );
}

function ManagerPanel({ d, act }) {
  const r = d.record;
  const [fdate, setFdate] = useState("");
  const [evType, setEvType] = useState(EVIDENCE_TYPES[0]);
  const [file, setFile] = useState(null);
  const locked = r.is_reference;

  const required = r.seva_snapshot?.evidence_required || [];
  const canAccept = r.status === "BOOKED";
  const canFulfil = ["BOOKED", "ACCEPTED"].includes(r.status);
  const canEvidence = ["ACCEPTED", "FULFILMENT_RECORDED", "EVIDENCE_RECEIVED"].includes(r.status);
  const canSubmit = ["FULFILMENT_RECORDED", "EVIDENCE_RECEIVED"].includes(r.status);

  const addEvidence = () => {
    const fd = new FormData();
    fd.append("evidence_type", evType);
    fd.append("submitted_by", "DEMO TEMPLE MANAGER");
    fd.append("source", "TEMPLE");
    if (file) fd.append("file", file);
    act(() => demo.addEvidence(r.record_id, fd), `Evidence submitted (${evType}). Submitted ≠ accepted.`).then(() => setFile(null));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="manager-panel">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="font-mono text-sm text-slate-700 bg-slate-100 px-2 py-0.5 rounded-sm">{r.record_id}</span>
        <StatusBadge status={r.status} />
        {locked && <SyntheticTag label="LOCKED REFERENCE" />}
        <Link to={`/records/${r.record_id}`} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">Verified Record <ArrowRight size={13} /></Link>
      </div>
      <div className="text-sm text-slate-700 mb-1">{r.seva_snapshot?.name} · {r.sankalpa?.devotee_name}</div>
      <div className="text-[11px] font-mono text-slate-400 mb-5">Evidence required: {required.join(", ")}</div>

      {locked ? (
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-4 text-sm text-slate-500">This is a locked reference demonstration and cannot be modified. Use “Start New Demo” or create a Devotee journey to act on a record.</div>
      ) : (
        <div className="space-y-5">
          <Row label="1 · Accept request">
            <Button size="sm" disabled={!canAccept} onClick={() => act(() => demo.accept(r.record_id), "Request accepted.")} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="btn-accept"><Check size={14} /> Accept</Button>
          </Row>
          <Row label="2 · Record fulfilment">
            <div className="flex gap-2 items-center">
              <Input type="date" value={fdate} onChange={(e) => setFdate(e.target.value)} disabled={!canFulfil} className="rounded-sm border-slate-300 w-44" data-testid="fulfil-date" />
              <Button size="sm" disabled={!canFulfil || !fdate} onClick={() => act(() => demo.fulfil(r.record_id, { fulfilment_date: fdate }), "Fulfilment recorded (not yet certified).")} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="btn-fulfil"><CalendarCheck size={14} /> Record</Button>
            </div>
          </Row>
          <Row label="3 · Attach evidence">
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={evType} onValueChange={setEvType}>
                <SelectTrigger className="rounded-sm border-slate-300 w-52" data-testid="evidence-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <input type="file" onChange={(e) => setFile(e.target.files[0])} disabled={!canEvidence} className="text-xs text-slate-600 file:mr-2 file:rounded-sm file:border file:border-slate-300 file:bg-slate-50 file:px-2 file:py-1" data-testid="evidence-file" />
              <Button size="sm" disabled={!canEvidence} onClick={addEvidence} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="btn-add-evidence"><Upload size={14} /> Submit</Button>
            </div>
          </Row>
          <Row label="4 · Submit for validation">
            <Button size="sm" disabled={!canSubmit} onClick={() => act(() => demo.submitValidation(r.record_id), "Submitted for authorised validation.")} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="btn-submit-validation"><SendHorizonal size={14} /> Submit</Button>
          </Row>

          {d.evidence.length > 0 && (
            <div className="border-t border-slate-100 pt-4" data-testid="manager-evidence">
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-2">Submitted evidence</div>
              {d.evidence.map((e) => (
                <div key={e.id} className="text-xs text-slate-600 font-mono break-all mb-1">{e.type} · {e.hash_type} · {e.validation_state} · {e.sha256.slice(0, 20)}…</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border border-slate-100 rounded-sm px-4 py-3">
      <span className="text-sm text-slate-600">{label}</span>
      {children}
    </div>
  );
}
